import React from 'react';
import { useStore } from '../../../store/useStore';
import type { MarketListing, MarketSlot, Resource } from '../../../types';
import { Plus, Lock, X, Check } from 'lucide-react';
import { Button } from '../../common/Button';
import { ItemIcon } from '../../common/ItemIcon';

interface MarketSlotProps {
    slot: MarketSlot;
    onUnlock: (id: number) => void;
    onClick: (slot: MarketSlot) => void;
    onCancel: (id: number) => void;
    onClaim: (id: number) => void;
}

export const MarketSlotItem: React.FC<MarketSlotProps> = ({ slot, onUnlock, onClick, onCancel, onClaim }) => {
    const market = useStore(state => state.market);

    if (!slot.unlocked) {
        // Calculate cost: 1000 * (id + 1)
        const cost = 1000 * (slot.id + 1);
        return (
            <div className="market-slot locked" onClick={() => onUnlock(slot.id)}>
                <Lock size={24} />
                <span className="unlock-cost">{cost} G</span>
                <span className="label">Unlock Slot {slot.id + 1}</span>
            </div>
        );
    }

    if (!slot.listing) {
        return (
            <div className="market-slot empty" onClick={() => onClick(slot)}>
                <Plus size={32} />
                <span className="label">Create Listing</span>
            </div>
        );
    }

    const { listing } = slot;
    const item = market[listing.itemId];
    const isSell = listing.type === 'sell';
    const progress = Math.min(100, (listing.filledQuantity / listing.totalQuantity) * 100);
    const totalValue = listing.totalQuantity * listing.pricePerUnit;

    // Partial claim logic
    const claimableAmount = listing.filledQuantity - (listing.claimedQuantity || 0);
    const hasClaimable = claimableAmount > 0;

    // What are we claiming?
    // If Sell order -> we claim Gold (amount * price)
    // If Buy order -> we claim Items (amount)
    const claimLabel = isSell
        ? `${claimableAmount * listing.pricePerUnit} G`
        : `${claimableAmount} Items`;

    return (
        <div className={`market-slot active ${listing.completed ? 'completed' : ''}`}>
            <div className="slot-header-compact">
                <span className={`type-badge ${isSell ? 'sell' : 'buy'}`}>{isSell ? 'SELL' : 'BUY'}</span>
                <span className="compact-item-name">{item.name}</span>
                {!listing.completed && (
                    <button className="cancel-btn-compact" onClick={(e) => { e.stopPropagation(); onCancel(slot.id); }} title="Cancel Listing">
                        <X size={14} />
                    </button>
                )}
            </div>

            <div className="slot-body-compact">
                <div className="item-icon-large">
                    <ItemIcon id={listing.itemId} size={42} />
                </div>

                <div className="stats-grid">
                    <div className="stat-item">
                        <span className="stat-label">Price</span>
                        <span className="stat-value">{listing.pricePerUnit} <span className="currency">G</span></span>
                    </div>
                    <div className="stat-item right-align">
                        <span className="stat-label">Total Val</span>
                        <span className="stat-value">{totalValue.toLocaleString()} <span className="currency">G</span></span>
                    </div>
                    <div className="stat-item full-width">
                        <span className="stat-label">Progress</span>
                        <span className="stat-value">{listing.filledQuantity} / {listing.totalQuantity}</span>
                    </div>
                </div>
            </div>

            <div className="slot-footer-compact">
                <div className="progress-container">
                    <div className="progress-bar-compact">
                        <div className="fill" style={{ width: `${progress}%` }} />
                    </div>
                </div>

                {hasClaimable ? (
                    <Button
                        size="sm"
                        variant="success"
                        className="claim-btn-compact"
                        onClick={(e) => { e.stopPropagation(); onClaim(slot.id); }}
                    >
                        Collect <span className="claim-amount">{claimLabel}</span>
                    </Button>
                ) : (
                    <div className="status-text">
                        {listing.completed ? 'Completed' : 'Active'}
                    </div>
                )}
            </div>
        </div>
    );
};
